import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, session as readSession } from '../../shell/bridge';
import { formatApiError } from '../../lib/errorCopy';
import { partnerLabel } from '../../lib/partnerName';
import { buildMonthGrid, collectMonthEvents, openingMonth, stepMonth } from '../../lib/planMonth';
import { Tooltip } from '../../ui/Tooltip';
import './plan.css';

/* Card zGRWc1QsfM3e (Jonathan email 2026-10-25, "move overview to
   tooltips/help"): the meeting overview is an in-context explanation, not a
   paragraph above the capture. Same pattern Requests already ships
   (ifd8sEdMo0JA), so nothing takes a line on this screen unless it is asked
   for. The four beats the meeting covers live here and in Help
   (web/src/lib/help.ts, life-plan), and nowhere else. */
const PLAN_OVERVIEW =
  'A weekly check-in on the four things a shared life needs: what you appreciated, ' +
  'what needs a decision or a share, what is coming up, and how you are both doing ' +
  '(“Anything that felt off, and anything worth celebrating?”). Take what is useful — ' +
  'it is your meeting, not a form. Fifteen minutes is plenty; Pear takes the notes.';

/**
 * Life → Plan (card 7aTCRQ9j8PWV, design v1.1 §5.6): the weekly meeting that
 * starts with a voice note and ends with cards.
 *
 * Capture (record / upload / type) → POST /api/plan/synthesise → the concierge
 * drafts four sections → the couple edits the draft → confirm files every line
 * as a task, request, Notebook entry or Thread message, each linked back to
 * the session through source_event_id. Nothing files before Confirm: the
 * quiet-AI rule is enforced by the fact that the draft is the only thing the
 * server ever stores until it is accepted.
 *
 * Meeting history sits underneath (date, status, what each line became), plus
 * the tasks the meetings filed — the table this card pulls forward from
 * Phase 2, so "Week plans" has somewhere to land that is not an ask.
 */

type SectionKey = 'appreciation' | 'week' | 'fun' | 'challenges';
type ItemKind = 'memory' | 'task' | 'request' | 'thread';
type Assignee = 'me' | 'partner' | 'anyone';

interface PlanItem {
  id: string;
  section: SectionKey;
  text: string;
  kind: ItemKind;
  assignee?: Assignee;
}

/** One calendar proposal from the meeting (card hCF9D2WCpv7Q). */
interface PlanCalendarDraft {
  id: string;
  text: string;
  /** The day the line named, 0=Sun … 6=Sat. */
  weekday: number;
  /** Suggested local hour, 0-23. */
  hour: number;
  status: 'proposed' | 'added' | 'dismissed';
  when: number | null;
  externalId: string | null;
  decidedBy: string | null;
  decidedAt: number | null;
}

type PlanDraft = Record<SectionKey, PlanItem[]> & { calendar?: PlanCalendarDraft[] };

/** One "Something to share" row waiting for this meeting (card a5fbGvPIggaR).
 *  A share reaches this screen only if both gates held: partner-visible AND
 *  flagged "Let's talk about it" — the server filters the first, this list the
 *  second, and a just-me row never arrives to be filtered at all. */
interface UsShare {
  shareId: string;
  text: string;
  talk: boolean;
  audience: 'shared' | 'secret';
  createdBy: string;
  createdAt: number;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The month grid's column heads, Sunday first to match `buildMonthGrid`. */
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The clock time a calendar row happens at, in the reader's own timezone. */
const fmtClock = (ms: number): string =>
  new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

interface PlanSpawned {
  kind: ItemKind;
  id: string;
  section: SectionKey;
  text: string;
}

interface PlanSession {
  sessionId: string;
  status: 'draft' | 'confirmed' | 'discarded';
  transcript: string;
  transcriptLength: number;
  durationSeconds: number | null;
  draft: PlanDraft | null;
  spawned: PlanSpawned[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  confirmedAt: number | null;
}

interface PlanTask {
  taskId: string;
  title: string;
  assignee: Assignee;
  state: 'open' | 'done';
  sourceEventId: string | null;
  createdBy: string;
  createdAt: number;
}

const SECTIONS: { key: SectionKey; label: string; hint: string }[] = [
  { key: 'appreciation', label: 'Appreciation', hint: 'What you were each glad of — saved as Collection memories.' },
  { key: 'week', label: 'Week plans', hint: 'Appointments, chores, who covers what — a task or an ask.' },
  { key: 'fun', label: 'Fun ahead', hint: 'Dates, trips, things to watch — Collection entries.' },
  { key: 'challenges', label: 'Challenges to address', hint: 'Open friction — an ask, or something to talk through.' },
];

/** What each section may file as — the same table the server clamps against. */
const SECTION_KINDS: Record<SectionKey, { value: ItemKind; label: string }[]> = {
  appreciation: [{ value: 'memory', label: 'Memory' }],
  week: [
    { value: 'task', label: 'Task' },
    { value: 'request', label: 'Ask' },
  ],
  fun: [{ value: 'memory', label: 'Collection entry' }],
  challenges: [
    { value: 'request', label: 'Ask' },
    { value: 'thread', label: 'Talk in Thread' },
  ],
};

const KIND_LABEL: Record<ItemKind, string> = {
  memory: 'Notebook',
  task: 'Task',
  request: 'Ask',
  thread: 'Thread',
};

const EMPTY_DRAFT: PlanDraft = { appreciation: [], week: [], fun: [], challenges: [] };

const countItems = (draft: PlanDraft | null): number =>
  draft ? SECTIONS.reduce((n, s) => n + draft[s.key].length, 0) : 0;

/** The section names as the summary says them out loud — "2 to appreciate,
 *  3 for the week", never "1 challenges to address". */
const SUMMARY_NOUNS: Record<SectionKey, string> = {
  appreciation: 'to appreciate',
  week: 'for the week ahead',
  fun: 'for fun',
  challenges: 'to work through',
};

/** The concierge's opening note on the draft (founder email 2026-10-28: a
 *  weekly meeting should open with a brief concierge summary in the
 *  concierge's own tone, not straight into four empty-looking sections).
 *  One sentence, built from what was actually captured — a count that is
 *  wrong is worse than no count, so the numbers come from the draft itself. */
function draftSummary(draft: PlanDraft): string {
  const counts = SECTIONS.map(sec => ({ n: draft[sec.key].length, noun: SUMMARY_NOUNS[sec.key] })).filter(c => c.n > 0);
  const total = counts.reduce((n, c) => n + c.n, 0);
  if (total === 0) {
    return 'I did not catch anything to file. Say it once more and I will listen again — nothing here is lost.';
  }
  const parts = counts.map(c => `${c.n} ${c.noun}`);
  const list =
    parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `I captured ${total} ${total === 1 ? 'thing' : 'things'} — ${list}. Nothing is filed until you agree with it: edit any line, drop what I misheard, then file the set.`;
}

/** The worker throws Error(JSON.stringify(body)) — pull the sentence out,
 *  in Title Case (card QR9phRSRQHNx). Shared with Thread through errorCopy. */
const readApiError = formatApiError;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result).split(',')[1] ?? '');
    fr.onerror = () => reject(new Error('Could Not Read That Recording'));
    fr.readAsDataURL(blob);
  });
}

/** The recording or file in hand, heard but not yet sent (card dGPk5Xx2iiS9):
 *  stopping is its own beat, so a mis-take costs nothing and nothing reaches
 *  the transcriber until the couple asks for it. */
interface HeldAudio {
  blob: Blob;
  label: string;
  seconds: number;
}

const fmtElapsed = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** A deep-enough copy of a stored draft so local edits never mutate the copy
 *  the session came back with (card DxFGCFSI4t0R). Calendar proposals ride
 *  along untouched — their verdicts are the server's. */
function cloneDraft(draft: PlanDraft): PlanDraft {
  const copy = {} as PlanDraft;
  for (const sec of SECTIONS) copy[sec.key] = draft[sec.key].map(item => ({ ...item }));
  if (draft.calendar) copy.calendar = draft.calendar.map(cal => ({ ...cal }));
  return copy;
}

/**
 * The four drafted sections, as one editor (card DxFGCFSI4t0R). Extracted so
 * the meeting being drafted and the meeting being *reopened* are the same
 * component: a reviewed capture shows its lines in the same list, and an edit
 * made there goes back through the same handler.
 *
 * `locked` is what a filed meeting gets: the lines can be reworded and a
 * task's assignee changed, but nothing can be added, removed or re-typed —
 * each line already became a real row, so its shape is the filing's.
 */
function DraftSections(props: {
  draft: PlanDraft;
  onPatch: (section: SectionKey, id: string, patch: Partial<PlanItem>) => void;
  onRemove?: (section: SectionKey, id: string) => void;
  onAdd?: (section: SectionKey) => void;
  locked?: boolean;
  /** Test-hook stem: 'plan-' while drafting, 'plan-saved-' after filing. */
  testPrefix: string;
}) {
  const { draft, onPatch, onRemove, onAdd, locked = false, testPrefix } = props;
  return (
    <>
      {SECTIONS.map(sec => (
        <section className="plan-section" key={sec.key} data-test={testPrefix + 'section-' + sec.key}>
          <h3 className="plan-section-title">{sec.label}</h3>
          <p className="plan-section-hint">{sec.hint}</p>
          {draft[sec.key].length === 0 && <p className="plan-empty">Nothing in this one.</p>}
          {draft[sec.key].map(item => (
            <div className="plan-item" key={item.id} data-test={testPrefix + 'item'}>
              <input
                className="plan-item-text"
                aria-label={sec.label + ' line'}
                value={item.text}
                onChange={e => onPatch(sec.key, item.id, { text: e.target.value })}
              />
              <select
                className="plan-item-kind"
                aria-label="Files as"
                value={item.kind}
                disabled={locked}
                onChange={e => onPatch(sec.key, item.id, { kind: e.target.value as ItemKind })}
              >
                {SECTION_KINDS[sec.key].map(k => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
              {item.kind === 'task' && (
                <select
                  className="plan-item-assignee"
                  aria-label="Who does it"
                  value={item.assignee ?? 'anyone'}
                  onChange={e => onPatch(sec.key, item.id, { assignee: e.target.value as Assignee })}
                >
                  <option value="anyone">Anyone</option>
                  <option value="me">Me</option>
                  <option value="partner">Partner</option>
                </select>
              )}
              {!locked && onRemove && (
                <button
                  type="button"
                  className="plan-item-remove"
                  aria-label="Remove this line"
                  onClick={() => onRemove(sec.key, item.id)}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {!locked && onAdd && (
            <button
              type="button"
              className="plan-add"
              data-test={testPrefix + 'add-' + sec.key}
              onClick={() => onAdd(sec.key)}
            >
              + Add a line
            </button>
          )}
        </section>
      ))}
    </>
  );
}

export function Plan() {
  /* idle → recording → stopped → busy(drafting) → draft → done.
     Card dGPk5Xx2iiS9: the founder's three asks are three taps — Start the
     meeting, Stop, Summarise discussion — instead of one button that began a
     recording and another that ended it *and* spent the transcription. */
  const [phase, setPhase] = useState<'idle' | 'recording' | 'stopped' | 'busy' | 'draft' | 'done'>('idle');
  const [held, setHeld] = useState<HeldAudio | null>(null);
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [plan, setPlan] = useState<PlanSession | null>(null);
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [history, setHistory] = useState<PlanSession[]>([]);
  const [tasks, setTasks] = useState<PlanTask[]>([]);
  const [agenda, setAgenda] = useState<UsShare[]>([]);
  /* The row whose dismiss is in flight (card 4rp5LQRsgi6W) — one at a time,
     so a fast double-tap cannot race two writes for the same row. */
  const [busyShare, setBusyShare] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openSession, setOpenSession] = useState<PlanSession | null>(null);
  /** The reopened meeting's lines, edited locally until Save (card DxFGCFSI4t0R). */
  const [openDraft, setOpenDraft] = useState<PlanDraft | null>(null);
  const [savingOpen, setSavingOpen] = useState(false);
  const [openNote, setOpenNote] = useState('');

  /* Card sp7XGAC5XB1H — the month grid. It is a READ of what the meetings
     already decided: `collectMonthEvents` keeps only rows with a verdict of
     `added` and a real time on them, so nothing here can create, move or
     imply an event that the worker did not already put on the calendar. */
  const monthEvents = useMemo(() => collectMonthEvents(history), [history]);
  const [shownMonth, setShownMonth] = useState(() => openingMonth([], Date.now()));
  /* Once the couple pages the grid, the month they chose wins over the
     opening month — a grid that jumps back on the next refresh is a bug, not
     a default. */
  const monthPicked = useRef(false);
  useEffect(() => {
    if (monthPicked.current) return;
    const next = openingMonth(monthEvents);
    setShownMonth(prev =>
      prev.year === next.year && prev.month === next.month ? prev : next,
    );
  }, [monthEvents]);
  const monthGrid = useMemo(
    () => buildMonthGrid(shownMonth.year, shownMonth.month, monthEvents),
    [shownMonth, monthEvents],
  );

  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  /* The elapsed seconds the recording actually ran: onstop runs in the
     recorder's closure, so the state variable would still read 0 there. */
  const secondsRef = useRef(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const canRecord = typeof navigator !== 'undefined' && typeof MediaRecorder !== 'undefined';

  const meId = readSession()?.memberId ?? '';

  const stopTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const refresh = useCallback(async () => {
    try {
      const [s, t, sh] = await Promise.all([
        api<{ sessions: PlanSession[] }>('/api/plan/sessions'),
        api<{ tasks: PlanTask[] }>('/api/plan/tasks'),
        api<{ shares: UsShare[] }>('/api/plan/shares'),
      ]);
      setHistory(s.sessions ?? []);
      setTasks(t.tasks ?? []);
      // The API answers newest-first; a talking point waits its turn, so the
      // agenda reads oldest-first. `talk && shared` is the whole gate here.
      setAgenda(
        (sh.shares ?? [])
          .filter(x => x.talk === true && x.audience === 'shared')
          .slice()
          .reverse(),
      );
    } catch {
      /* history is not what the meeting flow blocks on */
    }
  }, []);

  useEffect(() => {
    void refresh();
    return () => {
      stopTimer();
      const rec = recRef.current;
      if (rec && rec.state === 'recording') rec.stop();
    };
  }, [refresh]);

  /**
   * Take one talking point off this week's agenda (card 4rp5LQRsgi6W).
   * Optimistic: the row leaves before the round-trip so the list does not
   * hang, and it is put back — oldest-first, where the server keeps it — if
   * the write fails, because a row silently lost on a failed write is the
   * bug this card is about. The share itself is untouched; only `talk`.
   */
  const dismissAgenda = async (shareId: string) => {
    if (busyShare) return;
    const removed = agenda.find(a => a.shareId === shareId) ?? null;
    if (!removed) return;
    setBusyShare(shareId);
    setError('');
    setAgenda(prev => prev.filter(a => a.shareId !== shareId));
    try {
      await api(`/api/plan/shares/${encodeURIComponent(shareId)}/dismiss`, {
        method: 'POST',
        body: {},
      });
    } catch (e) {
      setAgenda(prev =>
        prev.some(a => a.shareId === shareId)
          ? prev
          : [...prev, removed].sort((a, b) => a.createdAt - b.createdAt),
      );
      setError(formatApiError(e));
    } finally {
      setBusyShare(null);
    }
  };

  const loadDraft = (out: { session: PlanSession; draft: PlanDraft }) => {
    setPlan(out.session);
    setDraft(out.draft);
    setPhase('draft');
    setError('');
  };

  const synthesise = async (payload: Record<string, unknown>) => {
    setPhase('busy');
    setError('');
    try {
      const out = await api<{ session: PlanSession; draft: PlanDraft }>('/api/plan/synthesise', { body: payload });
      loadDraft(out);
    } catch (e) {
      setError(readApiError(e));
      setPhase('idle');
    }
  };

  const sendBlob = async (blob: Blob, filename: string) => {
    const audio = await blobToBase64(blob);
    await synthesise({ audio, mime: blob.type || 'audio/webm', filename });
  };

  const startRecording = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = ev => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        stopTimer();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (blob.size === 0) {
          setError('Nothing Was Recorded — Try Again or Upload a File');
          setPhase('idle');
          return;
        }
        /* Card dGPk5Xx2iiS9: Stop holds the audio, it does not spend it. The
           couple reads what is in hand and asks for the summary next. */
        setHeld({ blob, label: 'plan.webm', seconds: secondsRef.current });
        setPhase('stopped');
      };
      recRef.current = rec;
      rec.start();
      secondsRef.current = 0;
      setSeconds(0);
      setPhase('recording');
      timerRef.current = window.setInterval(() => {
        secondsRef.current += 1;
        setSeconds(secondsRef.current);
      }, 1000);
    } catch {
      setError('Microphone Unavailable — Upload a Recording Instead');
      setPhase('idle');
    }
  };

  const stopRecording = () => {
    stopTimer();
    const rec = recRef.current;
    /* Guarded: a second tap on the gap between stop() and onstop would throw
       InvalidStateError, and the recording is already being wrapped up. */
    if (!rec || rec.state !== 'recording') return;
    rec.stop();
  };

  /* Card dGPk5Xx2iiS9 — the summary is asked for, never assumed. One tap here
     is the first thing that leaves the device. */
  const summarise = async () => {
    if (!held) return;
    await sendBlob(held.blob, held.label);
  };

  const startOver = () => {
    setHeld(null);
    setSeconds(0);
    secondsRef.current = 0;
    setError('');
    setPhase('idle');
  };

  const patchItem = (section: SectionKey, id: string, patch: Partial<PlanItem>) => {
    setDraft(prev => (prev ? { ...prev, [section]: prev[section].map(i => (i.id === id ? { ...i, ...patch } : i)) } : prev));
  };

  const removeItem = (section: SectionKey, id: string) => {
    setDraft(prev => (prev ? { ...prev, [section]: prev[section].filter(i => i.id !== id) } : prev));
  };

  const addItem = (section: SectionKey) => {
    setDraft(prev =>
      prev
        ? {
            ...prev,
            [section]: [
              ...prev[section],
              {
                id: crypto.randomUUID(),
                section,
                text: '',
                kind: SECTION_KINDS[section][0].value,
                assignee: 'anyone',
              },
            ],
          }
        : prev,
    );
  };

  const confirm = async () => {
    if (!plan || !draft) return;
    setPhase('busy');
    setError('');
    try {
      const out = await api<{ session: PlanSession }>(
        '/api/plan/sessions/' + plan.sessionId + '/confirm',
        { body: { draft } },
      );
      setPlan(out.session);
      setPhase('done');
      void refresh();
    } catch (e) {
      setError(readApiError(e));
      setPhase('draft');
    }
  };

  const discard = async () => {
    if (!plan) return;
    setPhase('busy');
    setError('');
    try {
      await api('/api/plan/sessions/' + plan.sessionId + '/discard', { method: 'POST', body: {} });
    } catch (e) {
      setError(readApiError(e));
    }
    setPlan(null);
    setDraft(null);
    /* The audio in hand is dropped with the draft — holding a recording of a
       conversation in memory after the couple said no is not ours to keep. */
    setHeld(null);
    setPhase('idle');
    void refresh();
  };

  /* Card hCF9D2WCpv7Q — decide one calendar proposal. The verdict is the
   * server's to record: `approve` writes the Google event first and only then
   * stamps the proposal `added` (a failed write leaves the tap retryable),
   * `dismiss` needs no Google at all. Either way the whole session comes back
   * and the block re-renders from it, so both partners read the same verdict. */
  const decideCalendar = async (draftId: string, decision: 'approve' | 'dismiss') => {
    if (!plan) return;
    setError('');
    try {
      const out = await api<{ session: PlanSession }>(
        '/api/plan/sessions/' + plan.sessionId + '/calendar/' + draftId + '/' + decision,
        { method: 'POST', body: {} },
      );
      setPlan(out.session);
      setDraft(prev => (prev && out.session.draft ? { ...prev, calendar: out.session.draft.calendar } : prev));
    } catch (e) {
      setError(readApiError(e));
    }
  };

  const reset = () => {
    setPlan(null);
    setDraft(null);
    setHeld(null);
    setError('');
    setSeconds(0);
    secondsRef.current = 0;
    setPhase('idle');
  };

  const openHistory = async (s: PlanSession) => {
    if (openId === s.sessionId) {
      setOpenId(null);
      setOpenSession(null);
      setOpenDraft(null);
      setOpenNote('');
      return;
    }
    try {
      const out = await api<{ session: PlanSession }>('/api/plan/sessions/' + s.sessionId);
      setOpenId(s.sessionId);
      setOpenSession(out.session);
      /* Card DxFGCFSI4t0R — the lines the meeting kept come back with it, so
         a capture reviewed yesterday still reads as the meeting it was. A
         session with no stored draft keeps the filed rows as the record. */
      setOpenDraft(out.session.draft ? cloneDraft(out.session.draft) : null);
      setOpenNote('');
    } catch (e) {
      setError(readApiError(e));
    }
  };

  /* Card sp7XGAC5XB1H — a chip on the month grid is the way to the thing
     itself: open the meeting the event came from, then bring its row into
     view so the tap lands on the item and not on a heading. */
  const focusMeeting = async (sessionId: string) => {
    const s = history.find(h => h.sessionId === sessionId);
    if (!s) return;
    monthPicked.current = true;
    if (openId !== sessionId) await openHistory(s);
    requestAnimationFrame(() => {
      const row = document.querySelector(`[data-plan-session="${sessionId}"]`);
      row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };

  const patchOpenItem = (section: SectionKey, id: string, patch: Partial<PlanItem>) => {
    setOpenDraft(prev =>
      prev ? { ...prev, [section]: prev[section].map(i => (i.id === id ? { ...i, ...patch } : i)) } : prev,
    );
    setOpenNote('');
  };

  /* Saving a reopened meeting. The server keeps the shape (same lines, same
     kinds) and pushes each edited line onto the row it filed, so the meeting
     and the task cannot disagree afterwards. */
  const saveOpenDraft = async () => {
    if (!openSession || !openDraft) return;
    setSavingOpen(true);
    setError('');
    setOpenNote('');
    try {
      const out = await api<{ session: PlanSession }>(
        '/api/plan/sessions/' + openSession.sessionId + '/amend',
        { body: { draft: openDraft } },
      );
      setOpenSession(out.session);
      setOpenDraft(out.session.draft ? cloneDraft(out.session.draft) : null);
      setOpenNote('Saved — the lines and what they filed now match.');
      void refresh();
    } catch (e) {
      setError(readApiError(e));
    } finally {
      setSavingOpen(false);
    }
  };

  /* Who the assignee label means: 'me' is whoever filed the row. */
  const assigneeLabel = (t: PlanTask): string => {
    if (t.assignee === 'anyone') return 'Anyone';
    const theirs = t.createdBy === meId ? t.assignee : t.assignee === 'me' ? 'partner' : 'me';
    return theirs === 'me' ? 'You' : 'Partner';
  };

  const filedCount = tasks.filter(t => t.state === 'open').length;

  return (
    <section className="plan" id="life-plan-panel" aria-labelledby="life-plan-title">
      <h2
        className="plan-title"
        id="life-plan-title"
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
      >
        Plan
        <Tooltip label={PLAN_OVERVIEW} tipFor="plan" />
      </h2>
      {/* ── agenda (card a5fbGvPIggaR) ─────────────────────────────────
          Shares flagged "Let's talk about it" from Us → Share. Both gates
          already held server-side and in refresh(), so anything here is a
          row both partners may read — this block only ever LISTED. It sits
          above the capture so the talking points are on screen before the
          recording starts, which is the whole point of a pre-meeting list. */}
      {agenda.length > 0 && (
        <div className="plan-agenda" data-test="plan-agenda">
          <h3 className="plan-section-title" data-test="plan-agenda-title">
            To talk about this week
          </h3>
          <p className="plan-section-hint">
            Put here from Us → Share by either of you. Read them first, then record the meeting
            as usual — the notes still take what is useful.
          </p>
          <ul className="plan-agenda-list" data-test="plan-agenda-list">
            {agenda.map(s => (
              <li className="plan-agenda-item" key={s.shareId} data-test="plan-agenda-item">
                <span className="plan-agenda-text">{s.text}</span>
                {/* Card 4rp5LQRsgi6W: the list only ever grew. Dismiss takes
                    the talking point off this week's agenda and nothing else —
                    the share itself stays in Us → Share, un-flagged. */}
                <button
                  type="button"
                  className="plan-btn plan-agenda-dismiss"
                  data-test="plan-agenda-dismiss"
                  aria-label={'Done — take off this week’s agenda: ' + s.text}
                  title="Done — take it off this week’s agenda"
                  disabled={busyShare === s.shareId}
                  onClick={() => void dismissAgenda(s.shareId)}
                >
                  Done
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* ── capture ─────────────────────────────────────────────────── */}
      {phase === 'idle' && (
        <div className="plan-capture" data-test="plan-capture">
          {/* Card zGRWc1QsfM3e: the intro paragraph, the four prompts and the
              closing line moved into the (?) tooltip beside the heading and
              into Help. The capture row is what is left, which is the point. */}
          <div className="plan-capture-row">
            {/* Secondary first, primary last (card ScKzwsv2DTEO): the founder
                read the pair as reversed — every other two-action row in the
                app ends on the button that commits, and the capture row put
                the loud action on the left. */}
            <button
              type="button"
              className="plan-btn"
              data-test="plan-upload"
              onClick={() => fileRef.current?.click()}
            >
              Upload a recording
            </button>
            {/* Card dGPk5Xx2iiS9 — the primary says what the tap does: it
                starts the meeting. Summarising is a later, separate ask. */}
            <button
              type="button"
              className="plan-btn plan-btn-primary"
              data-test="plan-record"
              onClick={() => void startRecording()}
              disabled={!canRecord}
            >
              Start the meeting
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="audio/*"
              className="plan-file"
              data-test="plan-file"
              onChange={e => {
                const file = e.target.files?.[0];
                e.target.value = '';
                /* An upload lands in the same "in hand" state as a recording,
                   so both capture paths end on the same verb (card
                   dGPk5Xx2iiS9) — and a wrong file is caught before it is
                   transcribed. */
                if (file) {
                  setHeld({ blob: file, label: file.name, seconds: 0 });
                  setError('');
                  setPhase('stopped');
                }
              }}
            />
          </div>
          {/* Card QR9phRSRQHNx — the typed-transcript path is gone: the meeting
              is captured by voice (or an uploaded recording), and the summary
              is asked for on its own button. */}
        </div>
      )}

      {phase === 'recording' && (
        <div className="plan-recording" data-test="plan-recording">
          <button
            type="button"
            className="plan-btn plan-btn-primary"
            data-test="plan-stop"
            onClick={stopRecording}
          >
            Stop
          </button>
          <span className="plan-elapsed" data-test="plan-elapsed">
            {fmtElapsed(seconds)}
          </span>
          <span className="plan-type" data-test="plan-recording-hint">
            Recording — say what is on your mind, then stop.
          </span>
        </div>
      )}

      {/* ── in hand, not yet sent (card dGPk5Xx2iiS9) ───────────────────
          The third ask lives here: what was captured is held on the device
          until "Summarise discussion" is tapped, so stopping is not a
          commitment and a mis-take costs nothing. */}
      {phase === 'stopped' && held && (
        <div className="plan-recording plan-stopped" data-test="plan-stopped">
          <span className="plan-held" data-test="plan-held">
            {held.seconds > 0
              ? `Recording ready — ${fmtElapsed(held.seconds)}`
              : `File ready — ${held.label}`}
          </span>
          <button type="button" className="plan-btn" data-test="plan-start-over" onClick={startOver}>
            Start over
          </button>
          <button
            type="button"
            className="plan-btn plan-btn-primary"
            data-test="plan-summarise"
            onClick={() => void summarise()}
          >
            Summarise discussion
          </button>
        </div>
      )}

      {phase === 'busy' && (
        <p className="plan-busy" data-test="plan-busy">
          {plan ? 'Filing…' : 'Transcribing and drafting…'}
        </p>
      )}

      {error && (
        <p className="plan-error" role="alert" data-test="plan-error">
          {error}
        </p>
      )}

      {/* ── the draft: edit before anything files ───────────────────── */}
      {phase === 'draft' && draft && (
        <div className="plan-draft" data-test="plan-draft">
          {/* Founder email 2026-10-28: the draft opens in the concierge's own
              voice — what it caught, and what happens next — so the four
              sections arrive as a note handed over rather than a form. */}
          <div className="plan-summary" data-test="plan-summary">
            <p className="plan-summary-title">Thank you for taking the time to plan your week.</p>
            <p className="plan-summary-body">{draftSummary(draft)}</p>
          </div>
          {/* Card dGPk5Xx2iiS9 — the transcript is what was captured, and the
              card asks for it to be readable here. Closed by default: the
              summary is the meeting, the transcript is the receipt. */}
          {plan?.transcript ? (
            <details className="plan-transcript-read" data-test="plan-draft-transcript">
              <summary>Read the transcript</summary>
              <p className="plan-transcript">{plan.transcript}</p>
            </details>
          ) : null}
          <DraftSections
            draft={draft}
            onPatch={patchItem}
            onRemove={removeItem}
            onAdd={addItem}
            testPrefix="plan-"
          />

          {/* Card hCF9D2WCpv7Q — the lines that name a day become calendar
              proposals. Nothing reaches Google until someone taps Add: the
              event is written first and the receipt appears only after the
              write succeeded, so what is on screen is always true. */}
          {(draft.calendar?.length ?? 0) > 0 && (
            <section className="plan-section plan-calendar" data-test="plan-calendar">
              <h3 className="plan-section-title">On the calendar</h3>
              <p className="plan-section-hint">
                From what you said — add one and it lands on your shared calendar. Not this one keeps it off.
              </p>
              {draft.calendar!.map(cal => (
                <div className="plan-cal-row" key={cal.id} data-test="plan-calendar-item">
                  <span className="plan-cal-text">{cal.text}</span>
                  <span className="plan-cal-when">
                    {WEEKDAYS[cal.weekday]} · {cal.hour % 12 === 0 ? 12 : cal.hour % 12}
                    {cal.hour >= 12 ? ' PM' : ' AM'}
                  </span>
                  {cal.status === 'proposed' ? (
                    <span className="plan-cal-actions">
                      <button
                        type="button"
                        className="plan-btn"
                        data-test="plan-calendar-dismiss"
                        onClick={() => void decideCalendar(cal.id, 'dismiss')}
                      >
                        Not this one
                      </button>
                      <button
                        type="button"
                        className="plan-btn plan-btn-primary"
                        data-test="plan-calendar-add"
                        onClick={() => void decideCalendar(cal.id, 'approve')}
                      >
                        Add to calendar
                      </button>
                    </span>
                  ) : cal.status === 'added' ? (
                    <span className="plan-cal-receipt" data-test="plan-calendar-added">
                      ✓ On your calendar — {WEEKDAYS[cal.weekday]} · {cal.hour % 12 === 0 ? 12 : cal.hour % 12}
                      {cal.hour >= 12 ? ' PM' : ' AM'}
                    </span>
                  ) : (
                    <span className="plan-cal-receipt plan-cal-receipt-muted" data-test="plan-calendar-dismissed">
                      Skipped
                    </span>
                  )}
                </div>
              ))}
            </section>
          )}

          <div className="plan-actions">
            {/* Secondary first, primary last (card ScKzwsv2DTEO) — same rule as
                the capture row above: Discard reads left, the commit reads
                right, which is the order every other action pair in Pear uses. */}
            <button type="button" className="plan-btn" data-test="plan-discard" onClick={() => void discard()}>
              Discard
            </button>
            <button
              type="button"
              className="plan-btn plan-btn-primary"
              data-test="plan-confirm"
              disabled={countItems(draft) === 0}
              onClick={() => void confirm()}
            >
              File {countItems(draft)} {countItems(draft) === 1 ? 'item' : 'items'}
            </button>
          </div>
        </div>
      )}

      {/* ── filed ───────────────────────────────────────────────────── */}
      {phase === 'done' && plan && (
        <div className="plan-done" data-test="plan-done">
          <h3 className="plan-section-title">Filed — {plan.spawned.length} items</h3>
          <ul className="plan-filed">
            {plan.spawned.map(s => (
              <li key={s.id} data-test="plan-filed-item">
                <span className={'plan-chip plan-chip-' + s.kind}>{KIND_LABEL[s.kind]}</span>
                {s.text}
              </li>
            ))}
          </ul>
          {/* Card y4XrBncGkQPu — the confirmation names who now holds this
              record. The rows above live in the couple's room, so the partner
              opens the same meeting and reads exactly these lines. */}
          <p className="plan-saved-both" data-test="plan-saved-both">
            {`Saved for both of you — ${partnerLabel()} sees this meeting and everything filed from it.`}
          </p>
          <button type="button" className="plan-btn" data-test="plan-again" onClick={reset}>
            Plan another week
          </button>
        </div>
      )}

      {/* ── open tasks the meetings filed ───────────────────────────── */}
      {tasks.length > 0 && (
        <section className="plan-tasks" aria-labelledby="plan-tasks-title" data-test="plan-tasks">
          <h3 className="plan-section-title" id="plan-tasks-title">
            Open from meetings ({filedCount})
          </h3>
          <ul className="plan-task-list">
            {tasks
              .filter(t => t.state === 'open')
              .slice(0, 12)
              .map(t => (
                <li key={t.taskId} data-test="plan-task">
                  <span className="plan-task-title">{t.title}</span>
                  <span className="plan-task-who">{assigneeLabel(t)}</span>
                </li>
              ))}
          </ul>
        </section>
      )}

      {/* ── the month (card sp7XGAC5XB1H) ───────────────────────────────
          The events Plan already put on the calendar, drawn on the days they
          happen. Read-only on purpose: every chip is a stored row, and a tap
          opens the meeting it came from. Nothing on this grid can write. */}
      <section className="plan-month" aria-labelledby="plan-month-title" data-test="plan-month">
        <div className="plan-month-head">
          <h3 className="plan-section-title" id="plan-month-title">
            On the calendar
          </h3>
          <div className="plan-month-nav">
            <button
              type="button"
              className="plan-month-step"
              data-test="plan-month-prev"
              aria-label="Previous month"
              onClick={() => {
                monthPicked.current = true;
                setShownMonth(m => stepMonth(m.year, m.month, -1));
              }}
            >
              ‹
            </button>
            <span className="plan-month-label" data-test="plan-month-label">
              {monthGrid.label}
            </span>
            <button
              type="button"
              className="plan-month-step"
              data-test="plan-month-next"
              aria-label="Next month"
              onClick={() => {
                monthPicked.current = true;
                setShownMonth(m => stepMonth(m.year, m.month, 1));
              }}
            >
              ›
            </button>
          </div>
        </div>
        {monthGrid.events.length === 0 && (
          <p className="plan-month-empty" data-test="plan-month-empty">
            Nothing on the calendar this month — events you add from a meeting land here.
          </p>
        )}
        <div className="plan-month-grid" data-test="plan-month-grid">
          <div className="plan-month-week plan-month-weekdays">
            {WEEKDAY_SHORT.map(d => (
              <span className="plan-month-weekday" key={d}>
                {d}
              </span>
            ))}
          </div>
          {monthGrid.weeks.map(week => (
            <div className="plan-month-week" key={week[0].key}>
              {week.map(day => (
                <div
                  key={day.key}
                  className={
                    'plan-month-day' +
                    (day.inMonth ? '' : ' is-outside') +
                    (day.isToday ? ' is-today' : '')
                  }
                  data-test="plan-month-day"
                  data-day={day.key}
                  data-events={day.events.length}
                  aria-label={
                    day.inMonth
                      ? `${day.date}${day.events.length > 0 ? ` — ${day.events.length} event${day.events.length === 1 ? '' : 's'}` : ''}`
                      : undefined
                  }
                >
                  <span className="plan-month-date">{day.inMonth ? day.date : ''}</span>
                  {day.events.map(ev => (
                    <button
                      type="button"
                      className="plan-month-event"
                      key={ev.draftId}
                      data-test="plan-month-event"
                      data-day={day.key}
                      title={`${ev.text} — ${fmtClock(ev.at)}`}
                      onClick={() => void focusMeeting(ev.sessionId)}
                    >
                      <span className="plan-month-event-time">{fmtClock(ev.at)}</span>
                      <span className="plan-month-event-text">{ev.text}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ── meeting history ─────────────────────────────────────────── */}
      {history.length > 0 && (
        <section className="plan-history" aria-labelledby="plan-history-title" data-test="plan-history">
          <h3 className="plan-section-title" id="plan-history-title">
            Meetings
          </h3>
          <ul className="plan-history-list">
            {history.map(s => (
              <li key={s.sessionId} className="plan-history-row" data-plan-session={s.sessionId}>
                <button
                  type="button"
                  className="plan-history-btn"
                  data-test="plan-history-row"
                  onClick={() => void openHistory(s)}
                >
                  <span className="plan-history-date">
                    {new Date(s.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                  </span>
                  <span className={'plan-status plan-status-' + s.status}>{s.status}</span>
                  <span className="plan-history-meta">
                    {countItems(s.draft)} drafted · {s.spawned.length} filed
                  </span>
                </button>
                {openId === s.sessionId && openSession && (
                  <div className="plan-history-open" data-test="plan-history-open">
                    {/* Card y4XrBncGkQPu — either partner opening a filed
                        meeting is told whose record it is. */}
                    {openSession.status === 'confirmed' && (
                      <p className="plan-saved-both" data-test="plan-saved-both-history">
                        {`Saved for both of you — ${partnerLabel()} sees this meeting and everything filed from it.`}
                      </p>
                    )}
                    {/* Card DxFGCFSI4t0R — the lines come first and stay
                        editable; the transcript is the receipt underneath,
                        closed until somebody asks for it. */}
                    {openDraft ? (
                      <>
                        <DraftSections
                          draft={openDraft}
                          onPatch={patchOpenItem}
                          locked
                          testPrefix="plan-saved-"
                        />
                        <div className="plan-actions plan-history-actions">
                          <button
                            type="button"
                            className="plan-btn plan-btn-primary"
                            data-test="plan-saved-save"
                            disabled={savingOpen}
                            onClick={() => void saveOpenDraft()}
                          >
                            {savingOpen ? 'Saving…' : 'Save changes'}
                          </button>
                        </div>
                        {openNote && (
                          <p className="plan-saved-note" data-test="plan-saved-note">
                            {openNote}
                          </p>
                        )}
                      </>
                    ) : (
                      <ul className="plan-filed">
                        {openSession.spawned.map(sp => (
                          <li key={sp.id}>
                            <span className={'plan-chip plan-chip-' + sp.kind}>{KIND_LABEL[sp.kind]}</span>
                            {sp.text}
                          </li>
                        ))}
                      </ul>
                    )}
                    {openDraft && openSession.spawned.length > 0 && (
                      <p className="plan-filed-beneath" data-test="plan-saved-filed-note">
                        {`Filed from this meeting: ${openSession.spawned.length}. Editing a line above edits what it filed.`}
                      </p>
                    )}
                    {openSession.transcript && (
                      <details className="plan-transcript-read" data-test="plan-history-transcript">
                        <summary>Read the transcript</summary>
                        <p className="plan-transcript">{openSession.transcript}</p>
                      </details>
                    )}
                    {/* Card hCF9D2WCpv7Q — a filed meeting still shows what
                        happened to each calendar proposal, so the partner who
                        did not tap it reads the same verdict. */}
                    {(openSession.draft?.calendar?.length ?? 0) > 0 && (
                      <ul className="plan-cal-history" data-test="plan-calendar-history">
                        {openSession.draft!.calendar!.map(cal => (
                          <li key={cal.id} data-test="plan-calendar-history-item">
                            <span className="plan-cal-text">{cal.text}</span>
                            <span className={'plan-cal-status plan-cal-status-' + cal.status}>
                              {cal.status === 'added'
                                ? 'On the calendar'
                                : cal.status === 'dismissed'
                                  ? 'Skipped'
                                  : 'Suggested'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}
